import { useCallback, useEffect } from 'react';
import { BareFetcher } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { CollectionsSearchResponse as Response } from '@gmm/sdk-voodoo/collections/search';
import { makeStore } from '~/lib/makeStore';
import { swrKeyFor, voodooSdkFetcher } from '~/lib/swr';
import { useGeographicalState } from '~/lib/trainerSettings';

export type SearchResults = Record<string, number>;

export interface SearchResultsState {
  isSearching: boolean;
  query: string;
  searchResults: SearchResults;
  totalHits: number;
}

const initialState: SearchResultsState = {
  isSearching: false,
  query: '',
  searchResults: {},
  totalHits: 0,
};

const [SearchResultsProvider, useSetSearchState, useSearchResults] = makeStore({
  name: 'SearchResults',
  initialState,
});

export { SearchResultsProvider, useSearchResults };

export const useSearch = (): SearchResultsState & {
  search: (query: string) => void;
} => {
  const { query, searchResults, totalHits } = useSearchResults();
  const setSearchState = useSetSearchState();
  const [geographicalState] = useGeographicalState();
  const swrKey = swrKeyFor(() =>
    query
      ? ['collections.search', { q: query, state: geographicalState }]
      : null,
  );

  const { data, isValidating: isSearching } = useSWRImmutable(swrKey, {
    fetcher: voodooSdkFetcher as BareFetcher<Response>,
  });

  useEffect(() => {
    if (!data) return;

    setSearchState(draft => {
      draft.searchResults = data.hits;
      draft.totalHits = data.totalHits;
    });
  }, [data, setSearchState]);

  const search = useCallback(
    (query: string): void => {
      if (!query) {
        return setSearchState(() => initialState);
      }

      setSearchState(draft => {
        draft.query = query;
      });
    },
    [setSearchState],
  );

  return {
    isSearching,
    query,
    search,
    searchResults,
    totalHits,
  };
};
